// @flow
import { isValidPhoneNumber, getParsedPhoneNumber } from '../../utils';
import {
  paymentServices,
  cartSystems,
  productTypes,
  OTHERS_VALUE,
  OTHER_CATEGORY,
  OTHER_CATEGORY_VALUE,
} from '../../constants';
import { ecSite1 } from '../../redux/business';

export const isEmpty = (input: string): boolean =>
  !input || typeof input !== 'string' || !input.trim().length;

const EMPTY_MESSAGE = '必須項目です';
const SELECT_EMPTY_MESSAGE = '選択してください';
const MAX_AGE = 130;
const URL_ERROR_MESSAGE = "http:// または https:// で始まるURLを入力してください。サイト未開設は「未定」と入力してください。";

const compareDate = (
  y1: number,
  m1: number,
  d1: number,
  y2: number,
  m2: number,
  d2: number
): boolean => y1 < y2 || (y1 === y2 && m1 < m2) || (y1 === y2 && m1 === m2 && d1 <= d2);

const validators = {
  email: (email: string) => {
    const re = /^([A-Za-z0-9_\-.+])+@([A-Za-z0-9_\-.])+\.([A-Za-z]{2,})$/;

    if (isEmpty(email)) {
      return EMPTY_MESSAGE;
    }

    if (!re.test(email)) {
      return 'メールアドレスを正しく入力してください';
    }

    return null;
  },
  phone: (phone: string) => {
    if (isEmpty(phone)) {
      return EMPTY_MESSAGE;
    }

    if (!isValidPhoneNumber(phone)) {
      return '携帯電話番号に誤りがあります';
    }

    const parsed = getParsedPhoneNumber(phone);

    // only accepts mobile, non-virtual phone numbers
    if (!/^(819|818|817)/.test(parsed)) {
      return 'この番号はご利用いただけません';
    }

    return null;
  },
  contactPhone: (phone: string) => {
    if (isEmpty(phone)) {
      return EMPTY_MESSAGE;
    }

    if (!/^\+?[0-9]{9,15}$/.test(phone)) {
      return '半角数字、8～15文字以内で入力してください';
    }

    return null;
  },
  number: (number: string) => {
    if (isEmpty(number)) {
      return EMPTY_MESSAGE;
    }

    if (!/^\d+$/.test(number)) {
      return '半角数字で入力してください';
    }

    return null;
  },
  corporateNumber: (corporateNumber: string) => {
    if (isEmpty(corporateNumber)) {
      return EMPTY_MESSAGE;
    }

    if (corporateNumber[0] === '0') {
      return '法人番号は国税庁法人番号公表サイトに表示される半角数字13桁でご入力ください。';
    }

    if (!/^(\d*)$/.test(corporateNumber) || corporateNumber.length !== 13) {
      return '半角数字13文字で入力してください。';
    }
  },
  bankCode: (bankCode: string) => {
    if (isEmpty(bankCode)) {
      return EMPTY_MESSAGE;
    }

    if (!/^(\d*)$/.test(bankCode) || bankCode.length !== 4) {
      return '4桁の半角数字で入力してください (例0123)';
    }

    return null;
  },
  bankBranchCode: (bankBranchCode: string) => {
    if (isEmpty(bankBranchCode)) {
      return EMPTY_MESSAGE;
    }

    if (!/^(\d*)$/.test(bankBranchCode) || bankBranchCode.length !== 3) {
      return '3桁の半角数字で入力してください (例012)';
    }

    return null;
  },
  specialCharacter: (input: string) => {
    if (isEmpty(input)) {
      return EMPTY_MESSAGE;
    }

    if (/[!,\\/@#$%?^&*)(+=.<>{}[\]:;'"|~`_-]/.test(input)) {
      return '特殊文字は入力できません';
    }

    return null;
  },
  bankAccountNumber: (bankAccountNumber: string) => {
    if (isEmpty(bankAccountNumber)) {
      return EMPTY_MESSAGE;
    }

    if (!/^(\d*)$/.test(bankAccountNumber) || bankAccountNumber.length !== 7) {
      return '7桁の半角数字で入力してください (例0123456)';
    }

    return null;
  },
  income: (income: string) => {
    if (isEmpty(income)) {
      return EMPTY_MESSAGE;
    }

    if (!/^(0|[1-9]\d*)$/.test(income)) {
      return '半角数字で入力してください';
    }

    return null;
  },
  zip: (zip: string) => {
    if (isEmpty(zip)) {
      return EMPTY_MESSAGE;
    }

    if (!/^\d{7}$/.test(zip)) {
      return '郵便番号に誤りがあります';
    }

    return null;
  },
  empty: (input: string) => {
    if (input === '-1') {
      return EMPTY_MESSAGE;
    }

    if (isEmpty(input)) {
      return EMPTY_MESSAGE;
    }

    return null;
  },
  url: (urlInput: string) => {
    const trimmedInput = urlInput.trim();

    if (trimmedInput === "") {
      return URL_ERROR_MESSAGE;
    }

    if (trimmedInput === "未定") {
      return null;
    }

    const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

    if (!urlPattern.test(trimmedInput)) {
      return URL_ERROR_MESSAGE;
    }

    return null;
  },
  cardSystem: (cardSystem: string) => {
    if (cartSystems.indexOf(cardSystem) < 0 && cardSystem !== OTHERS_VALUE) {
      return SELECT_EMPTY_MESSAGE;
    }

    return null;
  },
  paymentService: (paymentService: boolean) => {
    if (paymentServices.indexOf(paymentService) < 0 && paymentService !== OTHERS_VALUE) {
      return SELECT_EMPTY_MESSAGE;
    }

    return null;
  },

  othersCategories: (dependField: string) => (_: string, props: Object) => (basis: string) => {
    if (props[dependField][OTHER_CATEGORY]?.[OTHER_CATEGORY_VALUE] === OTHER_CATEGORY_VALUE) {
      if (isEmpty(basis)) {
        return EMPTY_MESSAGE;
      }
    }

    return null;
  },
  others: (dependField: string, dependValue: string) => (_: string, props: Object) => (
    basis: string
  ) => {
    if (props[dependField] === dependValue) {
      if (isEmpty(basis)) {
        return EMPTY_MESSAGE;
      }
    }

    return null;
  },
  statementMonth: (statementMonth: string) => {
    if (
      ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'].indexOf(statementMonth) < 0
    ) {
      return EMPTY_MESSAGE;
    }

    return null;
  },
  bankAccountType: (bankAccountType: string) => {
    if (['普通', '当座'].indexOf(bankAccountType) < 0) {
      return EMPTY_MESSAGE;
    }

    return null;
  },
  ecSiteCount: (ecSiteCount: string) => {
    if (['1', '2', '3', '4', '5', '5+'].indexOf(ecSiteCount) < 0) {
      return EMPTY_MESSAGE;
    }

    return null;
  },
  categoryCount: (categories: string) => {
    const categoriesLength = Object.keys(categories).length;
    // Get the number of subcategories selected
    const subCategoriesLength = Object.keys(categories).reduce((acc, category) => {
      acc += Object.keys(categories[category]).length;

      return acc;
    }, 0);

    if (!categoriesLength || !subCategoriesLength) {
      return 'カテゴリーを最大３つまでお選びください。';
    }
  },
  productType: (productType: string) => {
    if (productTypes.indexOf(productType) < 0 && productType !== OTHERS_VALUE) {
      return EMPTY_MESSAGE;
    }

    return null;
  },
  dateOfBirth: (dateOfBirth: string) => {
    const daysInMonth = ({ year, month }: { year: number, month: number }) =>
      new Date(year, month, 0).getDate();
    const reDateOfBirth = /^\d{4}-\d{2}-\d{2}$/;

    if (!reDateOfBirth.test(dateOfBirth)) {
      return '生年月日を正しく入力してください';
    }

    // at this point we know the format is valid
    const [year, month, date] = dateOfBirth.split('-').map(v => +v);

    if (daysInMonth({ year, month }) < date) {
      return '生年月日を正しく入力してください';
    }

    const now = new Date();
    const thisYear = now.getFullYear();
    const thisMonth = now.getMonth() + 1;
    const today = now.getDate();

    if (
      compareDate(year + MAX_AGE, month, date, thisYear, thisMonth, today) ||
      compareDate(thisYear, thisMonth, today, year, month, date)
    ) {
      return '入力された生年月日ではお申込みいただけません';
    }

    return null;
  },
  katakana: (katakana: string) => {
    if (isEmpty(katakana)) {
      return EMPTY_MESSAGE;
    }

    /**
     * 1. Consumer must enter only full-width Katakana (zenkaku 全角).
     * 2. Kana name can contain katakana, the  “ー” and special case ”ヴ”.
     * 3. Alphabetic characters are "not" allowed.
     * regex ref: https://gist.github.com/oanhnn/9043867
     */
    if (!/^([\s ァ-ン]|ー|ヴ)+$/.test(katakana)) {
      return '全角カナで入力してください';
    }

    if (/[\u0020]/.test(katakana)) {
      // return ‘Single byte space character entry is not allowed. Please remove spaces and retry.’ (in Japanese)
      return 'バイト単位のスペース文字の入力は許可されていません。スペースを削除して再試行してください。';
    }

    return null;
  },
  banktitle: (banktitle: string) => {
    if (isEmpty(banktitle)) {
      return EMPTY_MESSAGE;
    }

    /**
     * https://www.tanshin.co.jp/business/netbk/pdf/zengin_moji.pdf
     */
    if (!(/^([ 0-9A-Zァ-ンｧ-ﾝﾞﾟ]|ｦ|\(|\)|｢|｣|\/|-|\.|,|¥|\x20)+$/.test(banktitle))) {
      return '半角カナで入力してください。長音は「-」(半角ハイフン)で入力してください。';
    }

    return null;
  },
  kanji: (kanji: string) => {
    if (isEmpty(kanji)) {
      return EMPTY_MESSAGE;
    }

    /**
     * 1. Kanji name can contain kanji, hiragana, katakana, alphabet and the “-” or “ー”.
     * 2. Consumer must be able to enter hankaku or zenkaku.
     * 3. Alphabetic characters are allowed.
     * regex ref: https://gist.github.com/oanhnn/9043867
     */
    if (!/^([ぁ-んァ-ンｧ-ﾝﾞﾟ一-龥A-Za-ｚ]|[\u4E00-\u9FAF]|-|ー)+$/.test(kanji)) {
      return '数字や記号 (%,*,#) は入力できません';
    }

    return null;
  },
  if: (tester: Function, validator: Function) => {
    return (_: String, props: Object) => {
      if (tester(_, props)) {
        return validator;
      }

      return null;
    };
  },
  combine: (validator1, validator2) => (v: string, props: Object) => {
    return validator1(v, props) || validator2(v, props);
  },
  emailForPaidyAllDifferent: (_: string, props: Object) => {
    const emailList = [
      props.ecSite_1_emailForPaidy,
      props.ecSite_2_emailForPaidy,
      props.ecSite_3_emailForPaidy,
      props.ecSite_4_emailForPaidy,
      props.ecSite_5_emailForPaidy,
    ].filter(v => !!v);

    if (!emailList.every((email, index) => emailList.indexOf(email) === index)) {
      return '1つのメールアドレスで複数のサイト登録をする事はできません。';
    }

    return null;
  },
};

export default validators;
